import { Box, Typography, useTheme } from '@mui/material';

import { HeadingTag, Maybe, TAGContentAlignment } from '@/types';
import {
	ServiceLineHeroButtonCollection,
	ServiceLineHeroBodyCopy,
	ServiceLineHeroHeadline,
	ContentTypeRichTextContent,
	AnalyticsContext,
} from '@/types/generated';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import { customEvent } from '@/utils';
import ContentfulButton from '@/components/ContentfulButton';
import resolveContentfulHeadingTag from '@/utils/resolveContentfulHeadingTag';

import {
	wrapper,
	textContainerStyles,
	ctaButtonWrapperStyles,
	teamTagEyebrowOverrideStyles,
} from './ServiceLineHeroContent.styles';

export interface IServiceLineHeroContent {
	headline?: Maybe<ServiceLineHeroHeadline>;
	bodyCopy?: Maybe<ServiceLineHeroBodyCopy>;
	buttons?: ServiceLineHeroButtonCollection['items'];
	analyticsContext?: Maybe<AnalyticsContext>;
	isDarkBackground: boolean;
	contentAlign?: Maybe<TAGContentAlignment>;
	eyebrow?: Maybe<string>;
	eyebrowHtag?: Maybe<HeadingTag>;
	isSmallScreen?: Maybe<boolean>;
	svgData?: Maybe<React.ReactNode>;
}

const analyticsClick = (context: Maybe<AnalyticsContext> | undefined) => {
	if (context) {
		customEvent('tagEvent', context, context.eventName);
	}
};

export default function ServiceLineHeroContent({
	headline,
	eyebrowHtag = 'normal',
	eyebrow,
	bodyCopy,
	buttons,
	isDarkBackground = false,
	contentAlign = 'Left',
	isSmallScreen,
	svgData,
}: Readonly<IServiceLineHeroContent>) {
	const theme = useTheme();
	const centerAligned = contentAlign === 'Center';

	return (
		<Box data-testid="section_content_service_line_hero" sx={wrapper(centerAligned)}>
			<Box sx={textContainerStyles(centerAligned)} data-testid={'service_line_hero_content_container'}>
				{!isSmallScreen && eyebrow && (
					<Typography
						data-testid="service_line_hero_eyebrow_text"
						variant="overline"
						component={resolveContentfulHeadingTag(eyebrowHtag as HeadingTag)}
						color={isDarkBackground ? 'text.light' : 'text.secondary'}
						sx={teamTagEyebrowOverrideStyles}
					>
						{eyebrow}
					</Typography>
				)}
				{!isSmallScreen ? (
					<>
						<Box sx={{ height: '2px', width: '100%', bgcolor: '#A7ADB0', marginBottom: '60px' }} />
						{svgData}
					</>
				) : null}

				{headline && (
					<ContentTypeRichText
						centerAlign={centerAligned}
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						contentMaxWidth="100%"
						backgroundColor="transparent"
						dataTestId="group_service_line_hero_headline"
						content={headline as ContentTypeRichTextContent}
						docProps={{
							margin: 0,
							sx: [
								{
									color: isDarkBackground ? 'text.light' : 'text.primary',
									fontSize: '30px',
									lineHeight: '40px',
									fontWeight: 300,
								},
								{
									[theme.breakpoints.down('sm')]: {
										fontSize: '30px',
										lineHeight: '40px',
										fontWeight: 300,
									},
								},
								{
									color: isDarkBackground ? 'text.light' : 'text.primary',
								},
							],
						}}
					/>
				)}

				{bodyCopy && (
					<ContentTypeRichText
						centerAlign={centerAligned}
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						contentMaxWidth="100%"
						dataTestId="group_service_line_hero_body_copy"
						backgroundColor="transparent"
						content={bodyCopy as ContentTypeRichTextContent}
						docProps={{
							variant: 'bodyMediumBook',
							...(isDarkBackground && { color: 'text.light' }),
						}}
					/>
				)}

				{buttons && buttons.length > 0 && (
					<Box data-testid="group_service_line_hero_cta_container" sx={ctaButtonWrapperStyles(centerAligned)}>
						{buttons.map((button) =>
							button ? (
								<ContentfulButton
									key={button.sys.id}
									{...button}
									dataTestId="service_line_hero_cta"
									onClick={() => analyticsClick(button.analyticsContext)}
									docProps={{
										marginTop: isSmallScreen ? '2.5rem' : 0,
										gap: '0.625rem',
										fontSize: '0.875rem',
										fontWeight: 700,
										lineHeight: '1.25rem',
										letterSpacing: '0.188rem',
										color: '#FFFF',
										backgroundColor: '#002855',
										fontFamily: 'Messina Sans',
									}}
								/>
							) : null
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
}
